import React, { useState, useEffect, useRef } from "react";
import { fetchContent, adminUpdateContent, adminResetContent, uploadImage } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, Upload, Loader2, Save, RotateCcw, X } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const Section = ({ title, children }) => (
  <div className="bg-white border border-black/10 p-6 mb-6">
    <h3 className="font-serif-display text-lg font-bold gpm-text-green mb-4">{title}</h3>
    {children}
  </div>
);

const HomeEditor = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef(null);
  const { toast } = useToast();

  const load = () => fetchContent("home").then(setC).catch(() => setC(null));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setArr = (k, i, patch) => setC((p) => ({ ...p, [k]: p[k].map((x, idx) => (idx === i ? (typeof x === "string" ? patch : { ...x, ...patch }) : x)) }));
  const addItem = (k, item) => setC((p) => ({ ...p, [k]: [...(p[k] || []), item] }));
  const removeItem = (k, i) => setC((p) => ({ ...p, [k]: p[k].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...c };
      delete payload.key;
      await adminUpdateContent("home", payload);
      toast({ title: "Home page saved", description: "Your changes are now live." });
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the Home page to the default content? This cannot be undone.")) return;
    const d = await adminResetContent("home");
    setC(d);
    toast({ title: "Reset to default" });
  };

  const uploadHero = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of list) urls.push((await uploadImage(f)).url);
      setC((p) => ({ ...p, heroImages: [...(p.heroImages || []), ...urls] }));
      toast({ title: `${urls.length} image(s) added` });
    } catch (e) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  if (!c) return <div className="py-20 text-center text-gray-500">Loading Home page…</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl font-bold gpm-text-green">Home Page</h2>
          <p className="text-sm text-gray-500">Edit the text and photos shown on the public home page.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold"><RotateCcw size={15} /> Reset</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
          </button>
        </div>
      </div>

      <Section title="Hero (top banner)">
        <div className="space-y-4">
          <div><label className={labelCls}>Small heading (eyebrow)</label><input className={inputCls} value={c.heroEyebrow || ""} onChange={(e) => set("heroEyebrow", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Main heading — line 1</label><input className={inputCls} value={c.heroHeadingLine1 || ""} onChange={(e) => set("heroHeadingLine1", e.target.value)} /></div>
            <div><label className={labelCls}>Main heading — line 2</label><input className={inputCls} value={c.heroHeadingLine2 || ""} onChange={(e) => set("heroHeadingLine2", e.target.value)} /></div>
          </div>
          <div><label className={labelCls}>Subtitle (shown when not featuring a car)</label><textarea rows={2} className={inputCls} value={c.heroSubtitle || ""} onChange={(e) => set("heroSubtitle", e.target.value)} /></div>
          <div><label className={labelCls}>Button label</label><input className={inputCls} value={c.heroButtonLabel || ""} onChange={(e) => set("heroButtonLabel", e.target.value)} /></div>

          <label className="flex items-center gap-2 text-sm p-3 bg-[#f4f2ea] rounded">
            <input type="checkbox" checked={c.useFeaturedInHero !== false} onChange={(e) => set("useFeaturedInHero", e.target.checked)} />
            Rotate <b>featured cars</b> in the hero (uncheck to use your own photos below instead)
          </label>

          <div>
            <label className={labelCls}>Hero photos (used when the option above is unchecked)</label>
            {(c.heroImages || []).length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                {c.heroImages.map((img, i) => (
                  <div key={i} className="relative group aspect-[3/2]">
                    <img src={img} alt="" className="w-full h-full object-cover rounded border border-black/10" />
                    <button type="button" onClick={() => removeItem("heroImages", i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadHero(e.target.files)} />
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photos
            </button>
          </div>
        </div>
      </Section>

      <Section title="Intro section">
        <div className="space-y-4">
          <div><label className={labelCls}>Eyebrow</label><input className={inputCls} value={c.introEyebrow || ""} onChange={(e) => set("introEyebrow", e.target.value)} /></div>
          <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.introTitle || ""} onChange={(e) => set("introTitle", e.target.value)} /></div>
          <div>
            <label className={labelCls}>Paragraphs</label>
            <div className="space-y-3">
              {(c.introParagraphs || []).map((p, i) => (
                <div key={i} className="flex gap-2">
                  <textarea rows={3} className={inputCls} value={p} onChange={(e) => setArr("introParagraphs", i, e.target.value)} />
                  <button type="button" onClick={() => removeItem("introParagraphs", i)} className="text-gray-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addItem("introParagraphs", "")} className="mt-2 flex items-center gap-1 text-sm gpm-text-green font-semibold"><Plus size={15} /> Add paragraph</button>
          </div>
          <div><label className={labelCls}>Bold tagline</label><input className={inputCls} value={c.introTagline || ""} onChange={(e) => set("introTagline", e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Section headings">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Featured — eyebrow</label><input className={inputCls} value={c.featuredEyebrow || ""} onChange={(e) => set("featuredEyebrow", e.target.value)} /></div>
          <div><label className={labelCls}>Featured — heading</label><input className={inputCls} value={c.featuredTitle || ""} onChange={(e) => set("featuredTitle", e.target.value)} /></div>
          <div><label className={labelCls}>Services — eyebrow</label><input className={inputCls} value={c.servicesEyebrow || ""} onChange={(e) => set("servicesEyebrow", e.target.value)} /></div>
          <div><label className={labelCls}>Services — heading</label><input className={inputCls} value={c.servicesTitle || ""} onChange={(e) => set("servicesTitle", e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Why GPM (values)">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className={labelCls}>Eyebrow</label><input className={inputCls} value={c.valuesEyebrow || ""} onChange={(e) => set("valuesEyebrow", e.target.value)} /></div>
          <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.valuesTitle || ""} onChange={(e) => set("valuesTitle", e.target.value)} /></div>
        </div>
        <div className="space-y-3">
          {(c.values || []).map((v, i) => (
            <div key={i} className="flex gap-2">
              <input className={`${inputCls} w-48 shrink-0`} placeholder="Title" value={v.title} onChange={(e) => setArr("values", i, { title: e.target.value })} />
              <textarea rows={2} className={inputCls} placeholder="Text" value={v.text} onChange={(e) => setArr("values", i, { text: e.target.value })} />
              <button type="button" onClick={() => removeItem("values", i)} className="text-gray-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addItem("values", { title: "", text: "" })} className="mt-2 flex items-center gap-1 text-sm gpm-text-green font-semibold"><Plus size={15} /> Add value</button>
      </Section>

      <Section title="Call to action (bottom)">
        <div className="space-y-4">
          <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.ctaTitle || ""} onChange={(e) => set("ctaTitle", e.target.value)} /></div>
          <div><label className={labelCls}>Text</label><textarea rows={2} className={inputCls} value={c.ctaText || ""} onChange={(e) => set("ctaText", e.target.value)} /></div>
          <div><label className={labelCls}>Button label</label><input className={inputCls} value={c.ctaButtonLabel || ""} onChange={(e) => set("ctaButtonLabel", e.target.value)} /></div>
        </div>
      </Section>

      <div className="flex justify-end pb-6">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-3 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>
    </div>
  );
};

export default HomeEditor;
